import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { css } from "@emotion/css";
import { useSocialCallback } from "../../features/auth/hooks/auth-api.hooks";
import type { SocialProvider } from "../../api/services/auth/types";

const container = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  width: "100%",
  gap: "var(--spacing-lg)",
  backgroundColor: "#FBFBFB",
});

const spinner = css({
  width: "48px",
  height: "48px",
  border: "4px solid rgba(var(--pcrgba-200), 0.2)",
  borderTop: "4px solid var(--pc-600)",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
});

const message = css({
  fontSize: "var(--fs-400)",
  color: "var(--pc-700)",
  textAlign: "center",
});

const errorMessage = css({
  fontSize: "var(--fs-400)",
  color: "var(--error-color)",
  textAlign: "center",
  maxWidth: "400px",
});

interface SocialCallbackProps {
  provider: SocialProvider;
}

export const SocialCallback: React.FC<SocialCallbackProps> = ({ provider }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { mutate: handleCallback } = useSocialCallback();

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    
    console.log("[SocialCallback] Callback page loaded", {
      provider,
      hasCode: !!code,
      hasState: !!state,
      code: code?.substring(0, 10) + "...",
      state: state?.substring(0, 10) + "...",
    });

    // If no code or state, show error
    if (!code || !state) {
      const errorParam = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");
      const errorMsg = errorDescription || errorParam || "Falha na autenticação. Código ou estado não encontrado.";
      console.error("[SocialCallback] Missing code or state", { errorParam, errorDescription });
      setError(errorMsg);
      return;
    }

    // Call the backend to exchange code for token
    console.log("[SocialCallback] Calling backend to exchange code for token...");
    handleCallback(
      { provider, code, state },
      {
        onSuccess: (response) => {
          console.log("[SocialCallback] ✅ Backend callback successful:", response);
          console.log("[SocialCallback] Checking if token cookie was set...");
          const cookieStr = document.cookie;
          console.log("[SocialCallback] Current cookies:", cookieStr);
          console.log("[SocialCallback] **CRITICAL DEBUG**", {
            hasCookies: cookieStr.length > 0,
            cookieList: cookieStr.split(';').map(c => c.trim()),
            hasXToken: cookieStr.includes('x-token'),
            location: window.location.href,
            domain: window.location.hostname,
          });
          
          // Get the redirect URL from session storage or use default
          const redirectUrl = sessionStorage.getItem("social_login_redirect_url") || "/";
          sessionStorage.removeItem("social_login_redirect_url");
          
          // The backend has set the x-token cookie with Domain=.anchoringressos.com.br
          // This means it's accessible to both anchoringressos.com.br and api.anchoringressos.com.br
          // AuthContext will fetch the profile which will use this cookie automatically
          
          console.log("[SocialCallback] Redirecting to:", redirectUrl);
          console.log("[SocialCallback] AuthContext will fetch profile with the cookie");
          
          // Navigate - AuthContext will handle profile fetch with cookie
          navigate(redirectUrl, { replace: true });
        },
        onError: (error: any) => {
          console.error("[SocialCallback] ❌ Backend callback FAILED", {
            status: error?.response?.status,
            statusText: error?.response?.statusText,
            data: error?.response?.data,
            message: error?.message,
            config: {
              url: error?.config?.url,
              method: error?.config?.method,
              baseURL: error?.config?.baseURL,
            },
          });
          const errorMsg =
            error?.response?.data?.message ||
            error?.message ||
            "Erro ao processar autenticação social. Tente novamente.";
          setError(errorMsg);
        },
      }
    );
  }, [searchParams, handleCallback, navigate, provider]);

  if (error) {
    return (
      <div className={container}>
        <div className={errorMessage}>
          <h2>Erro na Autenticação</h2>
          <p>{error}</p>
          <button
            onClick={() => navigate("/entrar", { replace: true })}
            style={{
              marginTop: "var(--spacing-lg)",
              padding: "var(--spacing-sm) var(--spacing-md)",
              backgroundColor: "var(--pc-600)",
              color: "white",
              border: "none",
              borderRadius: "var(--spacing-2xs)",
              cursor: "pointer",
              fontSize: "var(--fs-400)",
            }}
          >
            Voltar para Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={container}>
      <div className={spinner}></div>
      <p className={message}>Processando autenticação com {provider}...</p>
    </div>
  );
};

export default SocialCallback;
